import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Plus, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

import { API_URL, ADMIN_KEY } from '../lib/config';
const PB_URL = 'https://api.simpleclair.fr';

// ==========================================
// Types
// ==========================================
interface ChainStep {
  id: string;
  type: string;
  status: string;
  step_order: number;
  description: string;
  error: string;
  retry_count: number;
  max_retries: number;
  started_at: string;
  completed_at: string;
}

interface Chain {
  chain_id: string;
  label: string;
  template: string;
  business_name: string;
  submission_id: string;
  status: string;
  total_steps: number;
  completed_steps: number;
  failed_steps: number;
  progress: number;
  current_step: {
    type: string;
    status: string;
    step_order: number;
    description: string;
  } | null;
  created: string;
  steps: ChainStep[];
}

interface ChainTemplate {
  key: string;
  label: string;
  description: string;
  steps_count: number;
}

// ==========================================
// Config affichage
// ==========================================
const STATUS_BADGES: Record<string, { emoji: string; label: string; color: string; bg: string }> = {
  queued: { emoji: '🟡', label: 'En attente', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
  in_progress: { emoji: '🔵', label: 'En cours', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  review: { emoji: '🟣', label: 'À valider', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
  done: { emoji: '🟢', label: 'Terminé', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  failed: { emoji: '🔴', label: 'Échoué', color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  completed: { emoji: '✅', label: 'Complète', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  rejected: { emoji: '🟠', label: 'Rejeté', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
  cancelled: { emoji: '⚪', label: 'Annulé', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

const JOB_TYPE_ICONS: Record<string, string> = {
  rebuild: '🔄',
  addon_logo: '🎨',
  addon_blog: '📝',
  addon_form: '📋',
  addon_gmb_create: '📍',
  addon_gmb_optimize: '🔧',
  addon_domain: '🌐',
  visibility_action: '📈',
  rejection_fix: '❌→✅',
  notify_client: '📧',
};

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}j`;
}

// ==========================================
// Composant : Barre de progression d'une chaîne
// ==========================================
function ChainProgress({ steps }: { steps: ChainStep[] }) {
  return (
    <div className="flex items-center gap-1 my-2">
      {steps.map((step, i) => {
        const badge = STATUS_BADGES[step.status] || STATUS_BADGES.queued;
        const isActive = step.status === 'in_progress' || step.status === 'review';
        return (
          <div key={step.id} className="flex items-center">
            {/* Connecteur */}
            {i > 0 && (
              <div className={`w-4 h-0.5 ${
                step.status === 'done' ? 'bg-green-400' : 'bg-gray-200'
              }`} />
            )}
            {/* Pastille étape */}
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-all ${
                step.status === 'done'
                  ? 'bg-green-100 border-green-400 text-green-700'
                  : step.status === 'failed'
                    ? 'bg-red-100 border-red-400 text-red-700'
                    : isActive
                      ? 'bg-blue-100 border-blue-400 text-blue-700 ring-2 ring-blue-200 animate-pulse'
                      : 'bg-gray-100 border-gray-300 text-gray-500'
              }`}
              title={`${step.description} (${badge.label})`}
            >
              {step.status === 'done' ? '✓' :
               step.status === 'failed' ? '✗' :
               isActive ? '⏳' :
               step.step_order}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==========================================
// Composant : Détail d'une chaîne (dépliable)
// ==========================================
function ChainDetail({ chain, onRetry }: { chain: Chain; onRetry: (jobId: string) => void }) {
  return (
    <div className="mt-3 space-y-2">
      {chain.steps.map(step => {
        const badge = STATUS_BADGES[step.status] || STATUS_BADGES.queued;
        const icon = JOB_TYPE_ICONS[step.type] || '⚙️';
        const isActive = step.status === 'in_progress' || step.status === 'review';

        return (
          <div
            key={step.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
              isActive ? 'border-blue-300 bg-blue-50/50' :
              step.status === 'done' ? 'border-green-200 bg-green-50/30' :
              step.status === 'failed' ? 'border-red-200 bg-red-50/30' :
              'border-gray-200 bg-white'
            }`}
          >
            {/* Numéro + icône */}
            <div className="flex items-center gap-2 w-10 flex-shrink-0">
              <span className="text-xs text-gray-400 font-mono">{step.step_order}.</span>
              <span className="text-lg">{icon}</span>
            </div>

            {/* Contenu */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 text-sm">{step.description}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${badge.bg} ${badge.color}`}>
                  {badge.emoji} {badge.label}
                </span>
              </div>
              {/* Retry info */}
              {step.retry_count > 0 && (
                <div className="text-xs text-orange-600 mt-0.5">
                  🔄 {step.retry_count}/{step.max_retries} tentatives
                </div>
              )}
              {/* Erreur */}
              {step.error && (
                <div className="text-xs text-red-600 mt-1 truncate" title={step.error}>
                  ❌ {step.error}
                </div>
              )}
            </div>

            {/* Temps */}
            <div className="text-right flex-shrink-0 w-16">
              {step.started_at && step.completed_at && (
                <div className="text-xs text-gray-500">
                  {(() => {
                    const ms = new Date(step.completed_at).getTime() - new Date(step.started_at).getTime();
                    const s = Math.floor(ms / 1000);
                    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}min`;
                  })()}
                </div>
              )}
            </div>

            {/* Action retry */}
            {step.status === 'failed' && (
              <button
                onClick={(e) => { e.stopPropagation(); onRetry(step.id); }}
                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors flex-shrink-0"
              >
                🔄 Relancer
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==========================================
// Modal : Créer une nouvelle chaîne
// ==========================================
function CreateChainModal({
  templates,
  onClose,
  onCreate
}: {
  templates: ChainTemplate[];
  onClose: () => void;
  onCreate: (template: string, submissionId: string) => void;
}) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Charger la liste des submissions (clients)
  useEffect(() => {
    fetch(`${PB_URL}/api/collections/submissions/records?sort=-created&perPage=50&fields=id,business_name,contact_email,addons,site_url`, {
      headers: { 'Authorization': `Bearer ${ADMIN_KEY}` }
    })
      .then(r => r.json())
      .then(data => setSubmissions(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-[90%] max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔗 Nouvelle chaîne de jobs</h3>

        {/* Sélection du template */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
          <div className="space-y-2">
            {templates.map(t => (
              <button
                key={t.key}
                onClick={() => setSelectedTemplate(t.key)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                  selectedTemplate === t.key
                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{t.description} ({t.steps_count} étapes max)</div>
              </button>
            ))}
          </div>
        </div>

        {/* Sélection du client */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
          {loading ? (
            <div className="text-sm text-gray-400">Chargement...</div>
          ) : (
            <select
              value={submissionId}
              onChange={e => setSubmissionId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400"
            >
              <option value="">Sélectionner un client...</option>
              {submissions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.business_name || s.contact_email || s.id}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 bg-gray-50 rounded-lg hover:bg-gray-100">
            Annuler
          </button>
          <button
            onClick={() => { if (selectedTemplate && submissionId) onCreate(selectedTemplate, submissionId); }}
            disabled={!selectedTemplate || !submissionId}
            className={`px-5 py-2 text-sm text-white rounded-lg font-medium ${
              selectedTemplate && submissionId
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-blue-300 cursor-not-allowed'
            }`}
          >
            Créer la chaîne
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Composant principal : Page Chaînes
// ==========================================
export default function Chains() {
  const [chains, setChains] = useState<Chain[]>([]);
  const [templates, setTemplates] = useState<ChainTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedChain, setExpandedChain] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchChains = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/chains`, {
        headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as any;
      setChains(data.chains || []);
      setTemplates(data.templates || []);
      setError(null);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChains(); }, [fetchChains]);

  // Auto-refresh toutes les 15s
  useEffect(() => {
    const interval = setInterval(fetchChains, 15000);
    return () => clearInterval(interval);
  }, [fetchChains]);

  const handleCreate = async (template: string, submissionId: string) => {
    try {
      const res = await fetch(`${API_URL}/admin/chains`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ADMIN_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ template, submission_id: submissionId }),
      });
      if (!res.ok) {
        const err = await res.json() as any;
        alert(`Erreur: ${err.error || 'Échec de création'}`);
        return;
      }
      setShowCreateModal(false);
      fetchChains();
    } catch (e: any) {
      alert(`Erreur: ${e.message}`);
    }
  };

  const handleRetry = async (jobId: string) => {
    try {
      await fetch(`${API_URL}/admin/jobs/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${ADMIN_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'queued', error: '', started_at: '', completed_at: '' }),
      });
      fetchChains();
    } catch (e) {
      console.error('Retry failed:', e);
    }
  };

  // Stats
  const activeChains = chains.filter(c => !['completed', 'cancelled'].includes(c.status)).length;
  const reviewChains = chains.filter(c => c.status === 'review').length;
  const completedChains = chains.filter(c => c.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement des chaînes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">🔗 Chaînes de jobs</h1>
          <p className="text-gray-500 mt-1">Pipelines séquentiels — chaque étape s'exécute après la précédente</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle chaîne
          </button>
          <button
            onClick={fetchChains}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="text-xs text-gray-400">{lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-blue-700">{activeChains}</div>
          <div className="text-xs text-blue-600">Chaînes actives</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-purple-700">{reviewChains}</div>
          <div className="text-xs text-purple-600">En attente de validation</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-green-700">{completedChains}</div>
          <div className="text-xs text-green-600">Complétées</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
          Erreur: {error}
        </div>
      )}

      {/* Liste des chaînes */}
      {chains.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 text-center py-16">
          <p className="text-lg text-gray-400">Aucune chaîne de jobs</p>
          <p className="text-sm text-gray-400 mt-1">Créez une chaîne pour orchestrer des actions séquentielles</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-5 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Créer une chaîne
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {chains.map(chain => {
            const isExpanded = expandedChain === chain.chain_id;
            const badge = STATUS_BADGES[chain.status] || STATUS_BADGES.queued;

            return (
              <div key={chain.chain_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* En-tête de la chaîne (cliquable) */}
                <div
                  className="px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedChain(isExpanded ? null : chain.chain_id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Chevron */}
                    <div className="text-gray-400 flex-shrink-0">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>

                    {/* Info principale */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-gray-900">{chain.business_name}</span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full border ${badge.bg} ${badge.color}`}>
                          {badge.emoji} {badge.label}
                        </span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{chain.label}</span>
                      </div>
                      {/* Barre de progression */}
                      <ChainProgress steps={chain.steps} />
                      {/* Étape courante */}
                      {chain.current_step && (
                        <div className="text-xs text-gray-500 mt-1">
                          Étape {chain.current_step.step_order}/{chain.total_steps} : {chain.current_step.description}
                        </div>
                      )}
                    </div>

                    {/* Progression chiffrée */}
                    <div className="text-right flex-shrink-0 w-24">
                      <div className="text-lg font-bold text-gray-700">{chain.progress}%</div>
                      <div className="text-xs text-gray-400">
                        {chain.completed_steps}/{chain.total_steps} étapes
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{timeAgo(chain.created)}</div>
                    </div>
                  </div>
                </div>

                {/* Détail déplié */}
                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-3">
                    <ChainDetail chain={chain} onRetry={handleRetry} />
                    <div className="mt-3 text-xs text-gray-400">
                      Chain ID: <code className="bg-gray-100 px-1 rounded">{chain.chain_id}</code>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal création */}
      {showCreateModal && (
        <CreateChainModal
          templates={templates}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
